/**
 * A caixa "Lançado no Financeiro", que mostra as despesas que a compra acabou de
 * lançar no caixa.
 *
 * Papel: lista cada despesa com o número do pedido e a data, a descrição, o
 * favorecido com a categoria e a subcategoria, e o valor, e só se fecha pelo
 * "Entendi" ou pela tecla Esc — não tem ação nenhuma sobre o que lista.
 *
 * Como usar: a caixa está aberta enquanto `despesas` tiver alguma linha, e quem
 * a monta esvazia a lista em `onFechar`. As linhas vêm de `despesasAvisadas`,
 * montadas só quando `lancaNaVirada` responde sim — o pedido que volta a
 * `comprado` mantendo a despesa de antes não ganha caixa, porque nada novo foi
 * lançado.
 */
import { formatarData, formatarDinheiro } from '@/shared/lib/formato';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { type DespesaAvisada } from '../lancado-no-financeiro';

interface AvisoLancadoNoFinanceiroProps {
  /** As despesas lançadas; vazia, a caixa fica fechada. */
  despesas: readonly DespesaAvisada[];
  /** Chamado pelo "Entendi" ou pela tecla Esc. */
  onFechar: () => void;
}

/**
 * A caixa que avisa o que foi lançado no Financeiro.
 *
 * @param despesas - As linhas de `despesasAvisadas`, de um ou de vários pedidos.
 * @param onFechar - Chamado ao fechar; é aí que quem monta deverá esvaziar a
 *   lista, ou a caixa continua aberta.
 * @returns A caixa, ou nada enquanto `despesas` estiver vazia.
 */
export function AvisoLancadoNoFinanceiro({
  despesas,
  onFechar,
}: AvisoLancadoNoFinanceiroProps) {
  return (
    <AlertDialog open={despesas.length > 0} onOpenChange={(aberto) => !aberto && onFechar()}>
      <AlertDialogContent>
        <AlertDialogHeader className="mb-0">
          <AlertDialogTitle>Lançado no Financeiro</AlertDialogTitle>
          <AlertDialogDescription>
            Este lançamento de despesa foi enviado automaticamente ao módulo Financeiro.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ul aria-label="Despesas lançadas" className="mt-3.5 mb-1 flex flex-col gap-2.5">
          {despesas.map((despesa, posicao) => (
            <li key={posicao} className="rounded-sm px-3 py-2.5">
              <p className="mb-1 font-mono text-[11px] text-texto-fraco">
                Pedido {despesa.numero} · {formatarData(despesa.data)}
              </p>
              <p className="mb-1.5 text-[13px] text-foreground">{despesa.descricao}</p>
              <div className="flex items-baseline justify-between gap-2.5 text-xs text-texto-medio">
                <span>
                  {despesa.favorecido ?? '—'}
                  {` · ${despesa.categoria}`}
                  {despesa.subcategoria !== null && ` / ${despesa.subcategoria}`}
                </span>
                <strong className="font-mono whitespace-nowrap text-brass-light">
                  {formatarDinheiro(despesa.valor)}
                </strong>
              </div>
            </li>
          ))}
        </ul>

        <AlertDialogFooter>
          <AlertDialogAction onClick={onFechar}>Entendi</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
